/**
 * Phase 2C.1 — Validation Drawer + Publish Readiness Chip.
 *
 * The drawer presents 60-rule validator output grouped into 8 collapsible
 * categories (Structure / Content / Scoring / Competencies / Configuration /
 * Publishing / Accessibility / Metadata). Each issue is click-to-focus and
 * shows severity + recommendation.
 *
 * The chip surfaces the derived Publish Readiness state
 * (Excellent / Good / Needs Attention / Not Ready) and the error/warning
 * counts so the author always knows where they stand at a glance.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  AlertCircle, AlertTriangle, CheckCircle2, ChevronDown, ChevronRight,
  Loader2, RefreshCw, ShieldCheck,
} from 'lucide-react';

/* ============================================================================
 *                          PUBLISH READINESS CHIP
 * ========================================================================== */
const READINESS_TONE = {
  Excellent:        { bg: 'bg-emerald-100 text-emerald-900 border-emerald-300 dark:bg-emerald-500/15 dark:text-emerald-200 dark:border-emerald-500/40', dot: 'bg-emerald-500' },
  Good:             { bg: 'bg-green-100 text-green-900 border-green-300 dark:bg-green-500/15 dark:text-green-200 dark:border-green-500/40', dot: 'bg-green-500' },
  'Needs Attention':{ bg: 'bg-amber-100 text-amber-900 border-amber-300 dark:bg-amber-500/15 dark:text-amber-200 dark:border-amber-500/40', dot: 'bg-amber-500' },
  'Not Ready':      { bg: 'bg-red-100 text-red-900 border-red-300 dark:bg-red-500/15 dark:text-red-200 dark:border-red-500/40', dot: 'bg-red-500' },
};

export function PublishReadinessChip({ envelope, loading, onClick, variant = 'compact' }) {
  const { t } = useTranslation();
  if (loading) {
    return (
      <button
        type="button"
        onClick={onClick}
        data-testid="studio-readiness-chip-loading"
        className="inline-flex items-center gap-1.5 px-2 h-7 text-xs rounded-full border border-border bg-muted/40"
      >
        <Loader2 className="h-3 w-3 animate-spin" />
        {t('studio.readiness.checking', { defaultValue: 'Checking…' })}
      </button>
    );
  }
  if (!envelope) {
    return (
      <button
        type="button"
        onClick={onClick}
        data-testid="studio-readiness-chip-idle"
        className="inline-flex items-center gap-1.5 px-2 h-7 text-xs rounded-full border border-border bg-card hover:bg-muted/40 transition-colors"
      >
        <ShieldCheck className="h-3 w-3" />
        {t('studio.readiness.runValidation', { defaultValue: 'Run validation' })}
      </button>
    );
  }
  const label = envelope.publish_readiness;
  const tone = READINESS_TONE[label] || READINESS_TONE['Not Ready'];
  const { error = 0, warning = 0 } = envelope.severity_counts || {};
  const localized = t(`studio.readiness.${label.replace(/\s+/g, '_').toLowerCase()}`, { defaultValue: label });
  if (variant === 'card') {
    return (
      <div
        data-testid="studio-readiness-card"
        onClick={onClick}
        className={`p-4 rounded-md border ${tone.bg} cursor-pointer hover:shadow-sm transition-shadow`}
      >
        <div className="flex items-center gap-2 mb-1.5">
          <span className={`inline-block h-2 w-2 rounded-full ${tone.dot}`} />
          <p className="text-xs uppercase tracking-wider font-semibold">
            {t('studio.readiness.title', { defaultValue: 'Publish Readiness' })}
          </p>
        </div>
        <p className="font-display text-lg font-bold">{localized}</p>
        <p className="text-xs mt-1 opacity-80">
          {error} {t('studio.readiness.errors', { defaultValue: 'errors' })} · {warning} {t('studio.readiness.warnings', { defaultValue: 'warnings' })}
        </p>
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid="studio-readiness-chip"
      title={localized}
      className={`inline-flex items-center gap-1.5 px-2 h-7 text-xs rounded-full border whitespace-nowrap shrink-0 ${tone.bg} hover:shadow-sm transition-shadow`}
    >
      <span className={`inline-block h-1.5 w-1.5 rounded-full ${tone.dot}`} />
      <span className="font-semibold">{localized}</span>
      {error > 0 && (
        <Badge variant="outline" className="text-xs h-4 px-1 bg-background/40">
          {error}E
        </Badge>
      )}
      {warning > 0 && (
        <Badge variant="outline" className="text-xs h-4 px-1 bg-background/40">
          {warning}W
        </Badge>
      )}
    </button>
  );
}

/* ============================================================================
 *                       VALIDATION DRAWER (right-side Sheet)
 * ========================================================================== */
export function ValidationDrawer({ open, onOpenChange, envelope, loading, onRevalidate, onFocusEntity, locale, persistKey }) {
  const { t, i18n } = useTranslation();
  const isEs = (locale || i18n.language || '').startsWith('es');

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-lg flex flex-col p-0" data-testid="studio-validation-drawer">
        <SheetHeader className="px-5 pt-5 pb-3 border-b border-border">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <SheetTitle className="text-base">
                {t('studio.validation.title', { defaultValue: 'Validation' })}
              </SheetTitle>
              <SheetDescription className="text-xs">
                {t('studio.validation.subtitle', { defaultValue: '60-rule check across 8 categories.' })}
              </SheetDescription>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={onRevalidate}
              disabled={loading}
              data-testid="studio-validation-rerun"
              className="h-7 text-xs gap-1"
            >
              {loading ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
              {t('studio.validation.rerun', { defaultValue: 'Re-run' })}
            </Button>
          </div>
          <div className="mt-3">
            <PublishReadinessChip envelope={envelope} loading={loading} variant="card" />
          </div>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-2 py-2">
          {loading && !envelope && (
            <div className="text-center py-12" data-testid="studio-validation-loading">
              <Loader2 className="h-6 w-6 animate-spin mx-auto text-primary" />
              <p className="text-xs text-muted-foreground mt-2">
                {t('studio.validation.runningHint', { defaultValue: 'Running validator…' })}
              </p>
            </div>
          )}
          {envelope && envelope.categories.map((cat) => (
            <CategoryBlock
              key={cat.key}
              cat={cat}
              isEs={isEs}
              onFocusEntity={onFocusEntity}
              persistKey={persistKey}
            />
          ))}
          {envelope && envelope.severity_counts.error === 0 && envelope.severity_counts.warning === 0 && (
            <div className="text-center py-10" data-testid="studio-validation-clean">
              <CheckCircle2 className="h-10 w-10 mx-auto text-success/70 mb-2" />
              <p className="text-sm font-medium">
                {t('studio.validation.clean', { defaultValue: 'No issues found.' })}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                {t('studio.validation.cleanHint', { defaultValue: 'This assessment is ready to publish.' })}
              </p>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function CategoryBlock({ cat, isEs, onFocusEntity, persistKey }) {
  const { t } = useTranslation();
  const localStorageKey = persistKey ? `studio.validation.expand.${persistKey}.${cat.key}` : null;
  const [expanded, setExpanded] = useState(() => {
    if (localStorageKey) {
      try {
        const stored = window.localStorage.getItem(localStorageKey);
        if (stored !== null) return stored === 'true';
      } catch { /* noop */ }
    }
    return true;
  });
  const label = isEs ? cat.label_es : cat.label_en;
  const { error, warning } = cat.counts;
  const hasAny = error + warning > 0;

  // Auto-collapse clean categories ONLY when no persisted choice exists.
  useEffect(() => {
    if (!hasAny && localStorageKey) {
      try {
        if (window.localStorage.getItem(localStorageKey) === null) setExpanded(false);
      } catch { /* noop */ }
    } else if (!hasAny) {
      setExpanded(false);
    }
  }, [hasAny, localStorageKey]);

  const toggle = () => {
    setExpanded((v) => {
      const next = !v;
      if (localStorageKey) {
        try { window.localStorage.setItem(localStorageKey, String(next)); } catch { /* noop */ }
      }
      return next;
    });
  };

  return (
    <div className="mb-1" data-testid={`studio-validation-category-${cat.key}`}>
      <button
        type="button"
        onClick={toggle}
        className="w-full flex items-center gap-2 px-3 py-2 hover:bg-muted/40 rounded-md transition-colors"
        data-testid={`studio-validation-category-toggle-${cat.key}`}
      >
        {expanded ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
        <span className="text-xs font-semibold flex-1 text-left">{label}</span>
        {error > 0 && (
          <Badge variant="outline" className="text-xs h-5 px-1.5 bg-red-100 text-red-900 border-red-300 dark:bg-red-500/15 dark:text-red-200 dark:border-red-500/40">
            {error} {t('studio.validation.errorsShort', { defaultValue: 'err' })}
          </Badge>
        )}
        {warning > 0 && (
          <Badge variant="outline" className="text-xs h-5 px-1.5 bg-amber-100 text-amber-900 border-amber-300 dark:bg-amber-500/15 dark:text-amber-200 dark:border-amber-500/40">
            {warning} {t('studio.validation.warningsShort', { defaultValue: 'warn' })}
          </Badge>
        )}
        {!hasAny && (
          <CheckCircle2 className="h-3 w-3 text-success/70" />
        )}
      </button>
      {expanded && cat.issues.length > 0 && (
        <ul className="space-y-1 px-2 pb-2" data-testid={`studio-validation-issues-${cat.key}`}>
          {cat.issues.map((it, idx) => (
            <IssueRow
              key={`${it.code}-${it.entity_id || 'asset'}-${idx}`}
              issue={it}
              isEs={isEs}
              onFocus={() => onFocusEntity && onFocusEntity(it)}
            />
          ))}
        </ul>
      )}
      {expanded && cat.issues.length === 0 && (
        <div
          className="mx-2 mb-2 px-2.5 py-1.5 rounded-md border-l-2 border-emerald-300 bg-emerald-50/50 dark:bg-emerald-500/10 dark:border-emerald-500/30 flex items-center gap-1.5"
          data-testid={`studio-validation-category-clean-${cat.key}`}
        >
          <CheckCircle2 className="h-3 w-3 text-emerald-600 shrink-0" />
          <p className="text-xs text-emerald-900 dark:text-emerald-200">
            {t('studio.validation.categoryClean', { defaultValue: 'No issues detected in this category.' })}
          </p>
        </div>
      )}
    </div>
  );
}

function IssueRow({ issue, isEs, onFocus }) {
  const Icon = issue.severity === 'error' ? AlertCircle : AlertTriangle;
  const message = (isEs && issue.message_es) ? issue.message_es : issue.message;
  const recommendation = (isEs && issue.recommendation_es) ? issue.recommendation_es : issue.recommendation;
  const tone = issue.severity === 'error'
    ? 'border-red-300 bg-red-50/50 dark:bg-red-500/10 dark:border-red-500/30'
    : 'border-amber-300 bg-amber-50/50 dark:bg-amber-500/10 dark:border-amber-500/30';
  const iconTone = issue.severity === 'error' ? 'text-destructive' : 'text-amber-600';
  const clickable = issue.entity_id && onFocus;
  return (
    <li>
      <button
        type="button"
        onClick={clickable ? onFocus : undefined}
        disabled={!clickable}
        data-testid={`studio-validation-issue-${issue.code}`}
        data-severity={issue.severity}
        data-entity-id={issue.entity_id || ''}
        className={`w-full text-left rounded-md border-l-2 px-2.5 py-1.5 ${tone} ${clickable ? 'hover:shadow-sm cursor-pointer' : 'cursor-default'} transition-shadow`}
      >
        <div className="flex items-start gap-1.5">
          <Icon className={`h-3 w-3 shrink-0 mt-0.5 ${iconTone}`} />
          <div className="min-w-0 flex-1">
            <p className="text-xs font-medium leading-snug">
              {message}
              {issue.stable_id && (
                <span className="font-mono text-xs ml-1.5 px-1 py-0.5 rounded bg-muted">{issue.stable_id}</span>
              )}
            </p>
            <p className="text-xs text-muted-foreground leading-snug mt-0.5">
              {recommendation}
            </p>
            <p className="text-xs text-muted-foreground/70 mt-1 font-mono">
              {issue.code}
            </p>
          </div>
        </div>
      </button>
    </li>
  );
}

/* ============================================================================
 *                       useValidation() hook
 * ========================================================================== */
export function useValidation(assetId, adminApi) {
  const [envelope, setEnvelope] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const run = async () => {
    if (!assetId) return;
    setLoading(true);
    setError(null);
    try {
      const env = await adminApi.studioValidate(assetId);
      setEnvelope(env);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  };
  return { envelope, loading, error, run };
}

/* ============================================================================
 *                       Derive status maps for Navigator
 * ========================================================================== */
/** Convert validator envelope → { contentStatus, sectionStatus } for QuestionNavigator. */
export function deriveNavigatorStatuses(envelope) {
  const contentStatus = {};
  const sectionStatus = {};
  if (!envelope) return { contentStatus, sectionStatus };
  for (const cat of envelope.categories || []) {
    for (const it of cat.issues || []) {
      if (!it.entity_id) continue;
      const target = it.entity_kind === 'section' ? sectionStatus : contentStatus;
      const current = target[it.entity_id];
      // Severity precedence: error > warning > complete
      if (it.severity === 'error') {
        target[it.entity_id] = 'error';
      } else if (it.severity === 'warning' && current !== 'error') {
        target[it.entity_id] = 'warning';
      }
    }
  }
  return { contentStatus, sectionStatus };
}

/* ============================================================================
 *                       LAST BULK ACTION TOAST (Phase 2C.1 founder ask)
 * ========================================================================== */
/**
 * Renders a sticky toast after every save that included a `bulk_*` action.
 * Displays a concise label (e.g. "Difficulty set to D4 on 4 items") and
 * an Undo button wired to the editor history hook. Auto-dismisses after 8s
 * or on next save.
 */
const BULK_LABEL_KEY = {
  bulk_weight_set:        'studio.bulk.label.weight_set',
  bulk_difficulty_set:    'studio.bulk.label.difficulty_set',
  bulk_type_set:          'studio.bulk.label.type_set',
  bulk_moved_to_section:  'studio.bulk.label.moved',
  bulk_competency_added:  'studio.bulk.label.competency_added',
  bulk_competency_removed:'studio.bulk.label.competency_removed',
  bulk_tag_added:         'studio.bulk.label.tag_added',
  bulk_tag_removed:       'studio.bulk.label.tag_removed',
  bulk_added:             'studio.bulk.label.added',
  bulk_deleted:           'studio.bulk.label.deleted',
};

export function LastBulkActionToast({ saveId, actions, onUndo }) {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(false);

  // Reset on every new save_id
  useEffect(() => {
    setDismissed(false);
    if (!saveId) return undefined;
    const handle = setTimeout(() => setDismissed(true), 8000);
    return () => clearTimeout(handle);
  }, [saveId]);

  const bulkAction = useMemo(() => {
    if (!actions) return null;
    return actions.find((a) => a.code && a.code.startsWith('bulk_'));
  }, [actions]);

  if (!bulkAction || dismissed) return null;

  const fallback = bulkAction.label || t('studio.bulk.label.generic', { defaultValue: 'Bulk update applied' });
  const labelKey = BULK_LABEL_KEY[bulkAction.code];
  const label = labelKey
    ? t(labelKey, {
        defaultValue: fallback,
        n: bulkAction.count || 0,
        to: bulkAction.to,
      })
    : fallback;

  return (
    <div
      data-testid="studio-bulk-undo-toast"
      data-action-code={bulkAction.code}
      className="fixed bottom-12 left-1/2 -translate-x-1/2 z-50 min-w-[300px] max-w-[520px] bg-card border border-primary/30 shadow-xl rounded-md px-4 py-2.5 flex items-center gap-3 animate-in fade-in slide-in-from-bottom-2"
    >
      <CheckCircle2 className="h-4 w-4 text-success shrink-0" />
      <p className="text-xs font-medium flex-1 truncate">{label}</p>
      <Button
        size="sm"
        variant="outline"
        onClick={() => { setDismissed(true); onUndo && onUndo(); }}
        data-testid="studio-bulk-undo-button"
        className="h-7 text-xs gap-1"
      >
        {t('common.undo', { defaultValue: 'Undo' })}
      </Button>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        className="text-xs text-muted-foreground hover:text-foreground"
        data-testid="studio-bulk-undo-dismiss"
        aria-label="Dismiss"
      >
        ×
      </button>
    </div>
  );
}
